/*
 * The operator session.
 *
 * Same storage shape as the recruiter app's, and a different question at the end of it. There,
 * signing in resolves to a tenant and a membership; here it resolves to an *operator*, and an
 * account that authenticates perfectly but holds no `platform.operator` row gets 403 rather than
 * a session.
 *
 * That distinction is why `status` has a `refused` state rather than collapsing into `anonymous`.
 * Sending a valid-but-unauthorised operator back through sign-in would loop forever: they would
 * authenticate again, successfully, and be refused again.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, createApiClient } from '@indovation/contracts';
import type { AccessPlatformOperatorReadResponse, ApiClient } from '@indovation/contracts';
import { config } from '../config.js';
import type { TokenSet } from './oidc.js';

const TOKEN_KEY = 'tf.admin.auth.tokens';

export function readStoredTokens(): TokenSet | null {
  const raw = sessionStorage.getItem(TOKEN_KEY);
  if (!raw) {
    return null;
  }
  try {
    const tokens = JSON.parse(raw) as TokenSet;
    return tokens.expiresAt > Date.now() ? tokens : null;
  } catch {
    return null;
  }
}

export function storeTokens(tokens: TokenSet): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export type SessionStatus = 'loading' | 'anonymous' | 'refused' | 'authenticated';

export interface Session {
  readonly status: SessionStatus;
  readonly operator: AccessPlatformOperatorReadResponse | null;
  readonly api: ApiClient;
  readonly signOut: () => void;
  readonly refresh: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [operator, setOperator] = useState<AccessPlatformOperatorReadResponse | null>(null);

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiUrl,
        getToken: () => readStoredTokens()?.accessToken ?? null,
      }),
    [],
  );

  const refresh = useCallback(async () => {
    if (!readStoredTokens()) {
      setOperator(null);
      setStatus('anonymous');
      return;
    }

    try {
      setOperator(await api.call('access.platform_operator.read'));
      setStatus('authenticated');
    } catch (error) {
      if (error instanceof ApiError && error.isForbidden) {
        // Authenticated and not an operator. The token is kept, deliberately: dropping it would
        // send them back to sign-in, where they would succeed and be refused again.
        setOperator(null);
        setStatus('refused');
        return;
      }

      clearTokens();
      setOperator(null);
      setStatus('anonymous');
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(() => {
    clearTokens();
    setOperator(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo<Session>(
    () => ({ status, operator, api, signOut, refresh }),
    [status, operator, api, signOut, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession was called outside a <SessionProvider>.');
  }
  return session;
}
